import { Status } from './common';

/**
 * Customer type enumeration
 * Matches API: 'Company' | 'Individual' (capitalized)
 */
export type CustomerType = 'Company' | 'Individual';

/**
 * Customer Contact interface
 * Supports multiple contacts per customer
 */
export interface CustomerContact {
  id: string;
  isPrimary: boolean;
  name: string;
  jobTitle?: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  fax?: string;
  email: string;
  // Address fields (structured)
  countryId?: number | null;
  country?: string;
  stateId?: number | null;
  state?: string;
  cityId?: number | null;
  city?: string;
  postcode?: string;
  address?: string;
  addressLang?: string;
  // Status
  isActive?: boolean;
}

/**
 * Agreed payment terms type
 */
export type AgreedTermsType = 'ETA' | 'ETD' | 'EOM' | 'Net' | 'COD' | 'Receipt' | 'DAD';

/**
 * Hold status type
 * @deprecated Use HoldStatus from '@/types/api/customer' instead
 * auto = Auto (automatic status)
 * on_hold = On Hold (trading suspended)
 * off_hold = Off Hold (trading resumed)
 */
export type HoldStatusType = 'auto' | 'on_hold' | 'off_hold';

/**
 * Tax type enumeration
 */
export type CustomerTaxType = 'GST' | 'VAT' | 'BusinessIncomeTax';

/**
 * Customer Location interface
 * Supports multiple locations per customer with shipping/billing defaults
 */
export interface CustomerLocation {
  id: string;
  isDefaultShipping?: boolean;
  isDefaultBilling?: boolean;
  locationName: string;
  countryId?: number | null;
  country: string;
  stateId: number | null;
  state: string;
  cityId: number | null;
  city: string;
  postcode: string;
  address: string;
  addressLang?: string;
  notes?: string;
  isActive?: boolean;
}

/**
 * Currency balance for customer
 */
export interface CurrencyBalance {
  currency: string;
  balance: number;
  unbilledAmount: number;
}

/**
 * Customer interface
 * Main customer entity for display
 */
export interface Customer {
  id: string;
  customerId: string; // Display ID like CUS-001
  customerCode: string; // Customer code with CUS prefix (e.g., CUS-0001) - auto-generated
  customerName: string;
  category: string; // Legacy: Primary category (for backward compatibility)
  categories?: string[]; // Multiple category IDs
  address: string;
  phone: string;
  email: string;
  status: Status;

  // Type
  type: CustomerType;

  // Location
  country: string;
  city: string;
  postalCode?: string;

  // Notes
  accountNotes?: string;
  deliveryNotes?: string;
  salesNotes?: string;

  // Contacts - Support for multiple contacts
  contacts: CustomerContact[];

  // Locations - Support for multiple locations
  locations: CustomerLocation[];

  // Financial Information
  agreedTerms?: AgreedTermsType;
  dueInDays?: number;
  hold: HoldStatusType;
  taxType?: CustomerTaxType;
  taxRate?: number;

  // Account Information (read-only)
  defaultReceivablesAccount?: string;
  priceLevel?: string;
  creditLimit?: number;

  // Balance Information (read-only)
  outstandingBalance: number;
  unbilledAmount: number;
  currencies: CurrencyBalance[];

  // Preference
  invoiceeId?: number;
  shippingInstruction?: string;
  defaultShippingMarkFront?: string;
  defaultShippingMarkSide?: string;

  // Relationships
  salesRepId?: number;
  parentCompanyId?: number;
  subsidiaryId?: number;
  defaultLocationId?: number;
  relatedOrganization?: string;
  activeOnlineAccount?: boolean;

  // Meta
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating/editing customers
 * Used in multi-step customer form wizard
 */
export interface CustomerFormData {
  // Step 1: Primary Information
  // Basic Information
  countryId: number;
  abn: string; // ABN / Tax ID
  type: CustomerType;
  customerName: string;
  alternativeName?: string;
  nickname?: string;

  // Other Info
  salesRepId?: number;
  parentCompanyId?: number;
  subsidiaryId?: number;
  defaultLocationId?: number;
  relatedOrganization?: string;
  activeOnlineAccount?: boolean;

  // Categories
  categoryIds?: number[];

  // Notes
  accountNotes?: string;
  deliveryNotes?: string;
  salesNotes?: string;

  // Contacts
  contacts: CustomerContact[];

  // Locations
  locations: CustomerLocation[];

  // Step 2: Financial Information
  agreedTerms?: AgreedTermsType;
  dueInDays?: number;
  hold: HoldStatusType;
  taxType?: CustomerTaxType;

  // Step 3: Preference
  invoiceeId?: number; // Contact ID
  shippingInstruction?: string;
  defaultShippingMarkFront?: string;
  defaultShippingMarkSide?: string;
}

/**
 * Customer filters for list page
 */
export interface CustomerFilters {
  search?: string;
  category?: string;
  categories?: string[];
  country?: string;
  status?: Status;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Agreed terms options with display labels
 */
export const AGREED_TERMS_OPTIONS: Array<{ value: AgreedTermsType; label: string; labelZh: string }> = [
  { value: 'ETA', label: 'ETA (Estimated Time of Arrival)', labelZh: 'ETA（預計到達時間）' },
  { value: 'ETD', label: 'ETD (Estimated Time of Departure)', labelZh: 'ETD（預計出發時間）' },
  { value: 'EOM', label: 'EOM (End of Month)', labelZh: 'EOM（月底結算）' },
  { value: 'Net', label: 'Net (Net Payment Term)', labelZh: 'Net（淨付款期限）' },
  { value: 'COD', label: 'COD (Cash on Delivery)', labelZh: 'COD（貨到付款）' },
  { value: 'Receipt', label: 'Receipt (Upon Receipt)', labelZh: 'Receipt（收貨付款）' },
  { value: 'DAD', label: 'DAD (Days After Delivery)', labelZh: 'DAD（交貨後天數）' },
];

/**
 * Hold status options
 * 1 = Auto (automatic status)
 * 2 = On Hold (trading suspended)
 * 3 = Off Hold (trading resumed)
 */
export const HOLD_STATUS_OPTIONS: Array<{ value: HoldStatusType; label: string; labelZh: string }> = [
  { value: 'auto', label: 'Auto', labelZh: '自動' },
  { value: 'on_hold', label: 'On Hold', labelZh: '暫停交易' },
  { value: 'off_hold', label: 'Off Hold', labelZh: '恢復交易' },
];

/**
 * Customer tax type options
 */
export const CUSTOMER_TAX_TYPE_OPTIONS: Array<{ value: CustomerTaxType; label: string; labelZh: string; rate?: number }> = [
  { value: 'GST', label: 'GST', labelZh: 'GST', rate: 10 },
  { value: 'VAT', label: 'VAT', labelZh: 'VAT', rate: 20 },
  { value: 'BusinessIncomeTax', label: 'Business Income Tax', labelZh: '營業所得稅', rate: 5 },
];

/**
 * Customer categories (shared with vendor or customer-specific)
 */
export const CUSTOMER_CATEGORIES = [
  'Retail',
  'Wholesale',
  'Corporate',
  'Government',
  'Non-Profit',
  'Individual',
  'International',
  'Domestic',
];

/**
 * Terms that require Due in Days input
 * Some terms like COD don't need days input
 */
export const TERMS_REQUIRING_DAYS: AgreedTermsType[] = ['ETA', 'ETD', 'Net', 'DAD'];

/**
 * Check if a term requires due in days input
 */
export function termRequiresDays(term: AgreedTermsType | undefined): boolean {
  if (!term) return false;
  return TERMS_REQUIRING_DAYS.includes(term);
}
